import React from "react";
import { Step } from "react-joyride";
import { VIEWER_ROLES } from "../constants/textconstants";

export interface TourConfig {
  steps: Step[];
  routesForStep: (string | undefined)[];
}

/**
 * Return role-specific guided tour config
 */
export const getGuidedTourConfig = (
  role?: string,
  showGovtApprovalTab?: boolean
): TourConfig => {
  // Default (CCM) steps
  const ccmSteps: Step[] = [
    {
      content: (
        <div>
          <h4>Your Region Summary</h4>
          <p>See overall stats for your region, including issued, pending, and partner-wise data.</p>
        </div>
      ),
      target: ".certificate-overview-container",
      title: "Step 1/7",
      placement: "bottom",
      disableBeacon: true,
      isFixed: true,
    },
    {
      target: ".all-requests-tab",
      content: (
        <div>
          <h4>Review Requests</h4>
          <p>Check requests submitted by partners and approve or reject based on details here.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 2/7",
      isFixed: true
    },
    {
      target: ".approve-button",
      content: (
        <div>
          <h4>Approve / Reject</h4>
          <p>Check the partner details carefully and choose Approve or Reject based on the documents and eligibility.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 3/7",
      isFixed: true,
    },
    {
      target: ".all-requests-tab-pendingdelivery",
      content: (
        <div>
          <h4>Deliver Certificates</h4>
          <p>See all approved certificates that are waiting to be handed over to partners here.</p>
        </div>
      ),
      disableBeacon: true,
      title: "Step 4/7",
      isFixed: true,
    },
    {
      target: ".delivery-button",
      content: (
        <div>
          <h4>Confirm Delivery</h4>
          <p>Enter the delivery code when you hand over the certificate to the partner.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 5/7",
      isFixed: true,
    },
    {
      target: ".all-requests-tab-completed",
      content: (
        <div>
          <h4>Completed Requests</h4>
          <p>See all approved, rejected, or delivered certificate requests here.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 6/7",
      isFixed: true,
    },
    {
      target: ".cancel-button",
      content: (
        <div>
          <h4>Cancel Option</h4>
          <p>You can cancel an active certificate from here if needed.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 7/7",
      isFixed: true,
    },
  ];

  // Default routes for CCM steps
  const ccmRoutes: (string | undefined)[] = [
    '/',
    '/all-requests/0',
    '/review-request/1',
    '/all-requests/1',
    '/review-request/2',
    '/all-requests/2',
    '/review-request/3'
  ];

  const ccmGovtApprovalSteps: Step[] = [
    {
      content: (
        <div>
          <h4>Your Region Summary</h4>
          <p>See overall stats for your region, including issued, pending, and partner-wise data.</p>
        </div>
      ),
      target: ".certificate-overview-container",
      title: "Step 1/3",
      placement: "bottom",
      disableBeacon: true,
      isFixed: true,
    },
    {
      target: ".all-requests-tab-completed",
      content: (
        <div>
          <h4>Completed Requests</h4>
          <p>See all approved, rejected, or delivered certificate requests here.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 2/3",
      isFixed: true,
    },
    {
      target: ".cancel-button",
      content: (
        <div>
          <h4>Cancel Option</h4>
          <p>You can cancel an active certificate from here if needed.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 3/3",
      isFixed: true,
    },
  ];

  const ccmGovtApprovalRoutes: (string | undefined)[] = [
    '/',
    '/all-requests/2',
    '/review-request/3',
  ];

  // CCMC & LM steps: If the LM/CCMC workflow differs, define here:
  const ccmcSteps: Step[] = [
    {
      target: ".certificate-overview-container",
      content: (
        <div>
          <h4>Your Region Summary</h4>
          <p>See overall stats for your region, including issued, pending, and partner-wise data.</p>
        </div>
      ),
      title: "Step 1/6",
      placement: "bottom",
      disableBeacon: true,
      isFixed: true,
    },
    {
      target: ".all-requests-tab",
      content: (
        <div>
          <h4>Review Requests</h4>
          <p>Check requests submitted by partners and approve or reject based on details here.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 2/7",
      isFixed: true
    },
    {
      target: ".approve-button",
      content: (
        <div>
          <h4>Approve / Reject</h4>
          <p>Check the partner details carefully and choose Approve or Reject based on the documents and eligibility.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 3/7",
      isFixed: true,
    },
    {
      target: ".cancel-button",
      content: (
        <div>
          <h4>Cancel Option</h4>
          <p>You can cancel an active certificate from here if needed.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 4/6",
      isFixed: true,
    },
    {
      target: ".assign-button",
      content: (
        <div>
          <h4>Booklet Management</h4>
          <p>Assign certificate booklets to your team and view assignment details.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 5/6",
      isFixed: true,
    },
    {
      target: ".view-details-first",
      content: (
        <div>
          <h4>Check Booklet Status</h4>
          <p>See how many certificates in this booklet are used, pending, or assigned to the partners.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 6/6",
      isFixed: true,
    },
  ];

  const ccmcRoutes: (string | undefined)[] = [
    '/',
    '/all-requests/0',
    '/review-request/1',
    '/review-request/3',
    '/assigned-certificates',
    '/assigned-certificates',
  ];

  const viewerSteps: Step[] = [
    ccmcSteps[0],
     {
      target: ".issued-certificate-container ",
      content: (
        <div>
          <h4>All Issued Principal Certificates</h4>
          <p>View all certificates issued in your geography and check their current status.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 2/3",
      isFixed: true
    },
    {
      target: ".view-details-button",
      content: (
        <div>
          <h4>Principal Certificate Details</h4>
          <p>View partner information, certificate number, issue date, expiry date, and status.</p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      title: "Step 3/3",
      isFixed: true
    },
  ];

  const viewerRoutes: (string | undefined)[] = [
    '/',
    '/issued-certificates',
    '/issued-certificates',
  ];

  const roleKey = (role || '').toUpperCase();
  const viewerRoleValues = [
    VIEWER_ROLES.CCMM,
    VIEWER_ROLES.UL,
    VIEWER_ROLES.CL,
    VIEWER_ROLES.VIEWER,
  ].map((item) => item.toUpperCase());

  if (viewerRoleValues.indexOf(roleKey) !== -1) {
    return { steps: viewerSteps, routesForStep: viewerRoutes };
  }

  if (roleKey === 'APPROVER' || roleKey === 'LICENSE_MANAGER') {
    if(roleKey === 'LICENSE_MANAGER' && showGovtApprovalTab) {
      return { steps: ccmGovtApprovalSteps, routesForStep: ccmGovtApprovalRoutes };
    }
    return { steps: ccmcSteps, routesForStep: ccmcRoutes };
  }

  if(roleKey === 'ISSUER') {
   
    return { steps: ccmSteps, routesForStep: ccmRoutes };
  }

  // default to CCM steps if role not matched

  return { steps: ccmSteps, routesForStep: ccmRoutes };
};